import {
    ExclamationIcon,
    TrashIcon,
} from '../Icon'
import Button from '../Button'
import {EmbedContext} from 'components/EmbedContext'
import {FileProps} from '../Send/types'
import Image from '../Image'
import {NinchatErrorType} from '../../api/types/errors'
import Progress from '../Progress'
import React from 'react'
import {SendFilesProps} from './types'
import {fileUtil} from '../../utils'
import styles from './styles/SendFilesView.module.scss'
import useLightbox from 'hooks/lightbox'
import {useTranslation} from 'react-i18next'

const SendFilesView = (props: SendFilesProps): React.ReactElement => {
    const {
        files,
        onRemove,
    } = props

    const {t} = useTranslation()
    const {open} = useLightbox()
    const embed = React.useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    const getErrorMessage = (errorType: string): string => {
        switch (errorType as NinchatErrorType) {
            case NinchatErrorType.NinchatPermissionDeniedError:
                return t('You don\'t have permission to send attachments to this channel')
            default:
                return t('Unknown error')
        }
    }

    const handleOpenFile = (file: FileProps): void => {
        if (isEmbedQueueChannel && file?.thumbnail) {
            open(file?.thumbnail, {
                name: file.name,
                size: file.size,
                type: file.type,
            }, {downloadDisabled: true})
        }
    }

    const handleThumblineClick = (file: FileProps) => (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault()
        handleOpenFile(file)
    }

    const renderFile = (file: FileProps): React.ReactElement => {
        const {
            name,
            thumbnail,
        } = file

        return (
            <div className={`rowParent ${styles.file}`} key={name}>
                {!!thumbnail && !file.error &&
                    <a className={`${styles.thumbnail}`} href='' onClick={handleThumblineClick(file)}>
                        <Image src={thumbnail} />
                    </a>}
                {!!file.error &&
                    <div className={`${styles.thumbnailError}`}>
                        <ExclamationIcon />
                    </div>}
                <div className={`flexChild ${styles.info} ${isEmbedQueueChannel ? styles.infoEmbed : ''}`}>
                    <div className='rowParent'>
                        <div className={`flexChild ${styles.filename} ${isEmbedQueueChannel ? styles.filenameEmbed : ''}`} >{name}</div>
                        {typeof file.progress !== 'undefined' && !file.error &&
                            <div className={styles.uploading}>{file.progress === 100 ? t('Uploaded') : t('Uploading {{progress}}%', {progress: file.progress})}</div>}
                    </div>
                    {typeof file.progress === 'undefined' && !file.error &&
                        <div
                            aria-label={fileUtil.formatBytes(file.size)}
                            className={`${styles.size}`}
                        >
                            {fileUtil.formatBytes(file.size)}
                        </div>}
                    {typeof file.progress !== 'undefined' && !file.error &&
                        <div className={styles.progress}>
                            <Progress progress={file.progress} />
                        </div>}
                    {!!file.error &&
                        <div className={styles.error}>{getErrorMessage(file.error.error_type)}</div>}
                </div>
                <div className={styles.actions}>
                    <Button color='text' disabled={Boolean(file.progress)} iconLeft={TrashIcon} onPress={(): void => onRemove(name)} title={t('Remove file')} />
                </div>
            </div>)
    }

    return (
        <div className={`flexChild columnParent ${styles.SendFilesView}`}>
            {files.map((file: FileProps) => renderFile(file))}
        </div>
    )
}

export default SendFilesView
